/**
 * OALogoVersionsApi - Finds the other branding versions of a product.
 *
 * The catalogue carries three branding generations - white logo, grey logo, and the new
 * branding - and a colour can exist in any two of them or in all three. This resolves the
 * siblings of the product on screen and says which version each one is, so
 * pdp-logo-versions.js can offer the one that still has the size.
 *
 * Where oa-logo-versions-map.js lists a handle it is authoritative, for the roles and for
 * the colour families both. Titles and the "-2" handle convention are what is left for the
 * products it does not list, and neither can tell a white logo from a grey one.
 *
 * The versions are NOT identifiable by handle or by an equal title. Live example:
 *
 *   updated   "Oner Seamless Fitted Tank Top New Branding | Black"
 *             oner-seamless-fitted-tank-top-new-branding-black   created 2026-07-28
 *   original  "Oner Seamless Fitted Tank Top | Black"
 *             oner-seamless-fitted-vest-black                    created 2026-05-20
 *
 * The handles have nothing in common, and the titles differ by the branding marker. What
 * does hold is that stripping that marker from the base title leaves the two identical,
 * and the colour half is the same on both — so that is what the matching is built on. The
 * "-2" handle convention is kept only as a last-resort fallback for pairs set up that way.
 *
 * Depends on OAStorefrontApi being loaded first.
 */
window.OALogoVersionsApi = {
  // handle -> { at, value }. Always go through cacheGet / cacheSet: entries expire.
  cache: new Map(),

  // Availability is what this is fetched for, so an entry held for the life of the page
  // would keep offering a size the sibling sold out ten minutes ago. Matches the TTL in
  // oa-products-api.js and pdp-color-switch.js.
  CACHE_TTL_MS: 5 * 60 * 1000,

  // A shopper works through a colour rail, and every colour is its own group of two or three
  // handles — each of which gets its own entry, alongside the `colour:` ones. Well above one
  // visit, low enough to stay bounded.
  CACHE_MAX: 90,

  // handle -> the request that will cache it. Written only on resolve, so without this a
  // pointer sweeping the rail fires a second document for a handle already in flight.
  inflight: {},

  // The branding versions, in the order they are shown — the six columns of the handle
  // spreadsheet. White and grey are separate roles: they used to share one 'original', which
  // is what let a white-logo colour into a grey-logo colour family — see siblingColours.
  //
  // 'plain' is a version rather than the absence of one. UnifiedMove uses it as its main
  // branding, so a colour there can exist as plain AND reflective AND new branding at once.
  ROLES: ['plain', 'white', 'grey', 'reflective', 'embroidered', 'updated'],

  // Phrases that name which branding a title belongs to, with the role each one means.
  // Order is load-bearing in parseTitle: the first match wins and is then removed from the
  // base, so the three-word forms must precede the bare two-word ones or 'white logo' would
  // classify "… with White Logo" and leave the "with" behind.
  //
  // Every phrase is stripped from the base title AND from the search prefix, which is what
  // lets a grey-logo PDP search "… Bra*" and match new-branding titles; leaving any of them
  // in would search "… Bra with Grey Logo*" and find only its own family. The bare forms are
  // here because UnifiedMove titles the version without the "with": "… Shorts White Logo".
  MARKERS: [
    { phrase: 'new branding', role: 'updated' },
    { phrase: 'updated branding', role: 'updated' },
    { phrase: 'updated logo', role: 'updated' },
    { phrase: 'new logo', role: 'updated' },
    { phrase: 'original branding', role: 'grey' },
    { phrase: 'original logo', role: 'grey' },
    { phrase: 'with grey logo', role: 'grey' },
    { phrase: 'with gray logo', role: 'grey' },
    { phrase: 'with white logo', role: 'white' },
    { phrase: 'with reflective logo', role: 'reflective' },
    { phrase: 'and reflective logo', role: 'reflective' },
    { phrase: 'with embroidered logo', role: 'embroidered' },
    { phrase: 'grey logo', role: 'grey' },
    { phrase: 'gray logo', role: 'grey' },
    { phrase: 'white logo', role: 'white' },
    { phrase: 'reflective logo', role: 'reflective' },
    { phrase: 'embroidered logo', role: 'embroidered' }
  ],

  // Returns undefined for a miss, so a cached "this product has no sibling" (null) is
  // told apart from "never looked" and is not re-fetched on every size click.
  cacheGet: function(key) {
    var entry = this.cache.get(key);
    if (!entry) return undefined;
    if (Date.now() - entry.at >= this.CACHE_TTL_MS) {
      this.cache.delete(key);
      return undefined;
    }
    return entry.value;
  },

  cacheSet: function(key, value) {
    this.cache.set(key, { at: Date.now(), value: value });
    if (this.cache.size > this.CACHE_MAX) this.prune();
  },

  // Drop what has expired, then the oldest of what is left — a Map iterates in insertion
  // order, so its first keys are the least recently written.
  prune: function() {
    var self = this;
    var now = Date.now();
    this.cache.forEach(function(entry, key) {
      if (now - entry.at >= self.CACHE_TTL_MS) self.cache.delete(key);
    });
    if (this.cache.size <= this.CACHE_MAX) return;
    var excess = this.cache.size - this.CACHE_MAX;
    var keys = this.cache.keys();
    for (var i = 0; i < excess; i++) this.cache.delete(keys.next().value);
  },

  /**
   * Split a product title into the parts the pairing is matched on.
   *
   * "Oner Seamless Fitted Tank Top New Branding | Black"
   *   -> { base: 'oner seamless fitted tank top', colour: 'black', marker: 'updated' }
   *
   * @param {string} title
   * @returns {{base: string, colour: string, marker: string|null, rawBase: string}}
   *   marker is one of ROLES, or null when the title says nothing about logos. Note that
   *   null is NOT 'plain': only the map states plain, because an unmarked title means
   *   'plain' on UnifiedMove and 'legacy, same family as its marked siblings' elsewhere.
   */
  parseTitle: function(title) {
    var parts = String(title || '').split('|');
    var rawBase = (parts[0] || '').replace(/™/g, ' ').trim();
    var colour = (parts[1] || '').trim().toLowerCase();
    var base = rawBase.toLowerCase();
    var marker = null;

    this.MARKERS.forEach(function(entry) {
      if (base.indexOf(entry.phrase) !== -1) {
        if (!marker) marker = entry.role;
        base = base.split(entry.phrase).join(' ');
      }
    });
    return {
      base: base.replace(/\s+/g, ' ').trim(),
      colour: colour.replace(/\s+/g, ' ').trim(),
      marker: marker,
      rawBase: rawBase
    };
  },

  // Two products are the same garment in the same colour when their marker-stripped base
  // titles are equal and their colour halves are equal. Equality, not a prefix test — a
  // prefix would pair "… Tank Top" with "… Tank Top Long".
  isPair: function(a, b) {
    if (!a.base || !b.base) return false;
    return a.base === b.base && a.colour === b.colour;
  },

  // createdAt is what separates the original logo from the updated one when the titles
  // carry no marker, and it appears in no other query in this theme — OAProductsApi does
  // not select it and its transform has no date field. So this file carries its own field
  // set rather than reusing that one.
  productFields: `
    id
    handle
    title
    tags
    createdAt
    availableForSale
    featuredImage { url }
    priceRange { minVariantPrice { amount } }
    compareAtPriceRange { minVariantPrice { amount } }
    variants(first: 100) {
      nodes {
        id
        sku
        title
        availableForSale
        price { amount }
        compareAtPrice { amount }
        selectedOptions { name value }
      }
    }`,

  // Step 1: find candidates, ids and titles only. Deliberately light — every colour of
  // both branding versions comes back, so a run of 40+ products is normal, and asking for
  // variants here would put the document over the Storefront complexity budget.
  // tag: and title: are the filters proven to work on this store (oa-color-variants-api.js
  // searches on exactly this shape); handle: is silently ignored here.
  searchQuery: `query findLogoVersionCandidates($searchQuery: String!) {
    products(first: 100, query: $searchQuery) {
      nodes {
        id
        handle
        title
        createdAt
        tags
      }
    }
  }`,

  // Step 2: the full record, for the two products that survived matching.
  detailsQuery: function() {
    return 'query getLogoVersionDetails($ids: [ID!]!) {\n' +
      '  nodes(ids: $ids) {\n    ... on Product {' + this.productFields + '\n    }\n  }\n}';
  },

  // Exact lookups, one aliased product(handle:) per handle. The fallback for when the
  // title match finds nothing, and the only place the "-2" naming is relied on. Aliases
  // are positional: handles contain hyphens and cannot be GraphQL aliases.
  buildHandleQuery: function(handles) {
    var fields = this.productFields;
    var selections = handles.map(function(h, i) {
      return '\n  p' + i + ': product(handle: ' + JSON.stringify(h) + ') {' + fields + '\n  }';
    }).join('');
    return 'query getLogoVersionsByHandles {' + selections + '\n}';
  },

  /**
   * Shape a GraphQL product into what pdp-logo-versions.js renders. Prices are integer
   * minor units, matching OAProductsApi and what Shopify.formatMoney expects.
   */
  transform: function(gql) {
    if (!gql) return null;
    var compareAt = gql.compareAtPriceRange && gql.compareAtPriceRange.minVariantPrice
      ? Math.round(parseFloat(gql.compareAtPriceRange.minVariantPrice.amount) * 100)
      : null;

    return {
      id: parseInt(OAStorefrontApi.toNumericId(gql.id), 10),
      handle: gql.handle,
      title: gql.title,
      tags: gql.tags || [],
      createdAt: gql.createdAt,
      available: gql.availableForSale,
      image: gql.featuredImage ? gql.featuredImage.url : null,
      price: Math.round(parseFloat(gql.priceRange.minVariantPrice.amount) * 100),
      compare_at_price: compareAt,
      variants: (gql.variants.nodes || []).map(function(v) {
        return {
          id: parseInt(OAStorefrontApi.toNumericId(v.id), 10),
          sku: v.sku,
          title: v.title,
          option1: v.selectedOptions[0] ? v.selectedOptions[0].value : null,
          option2: v.selectedOptions[1] ? v.selectedOptions[1].value : null,
          available: v.availableForSale,
          price: Math.round(parseFloat(v.price.amount) * 100),
          compare_at_price: v.compareAtPrice
            ? Math.round(parseFloat(v.compareAtPrice.amount) * 100)
            : null
        };
      })
    };
  },

  hasTag: function(tags, tag) {
    if (!tag || !Array.isArray(tags)) return false;
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).toLowerCase() === String(tag).toLowerCase()) return true;
    }
    return false;
  },

  // Values with no whitespace go unquoted, which is the form proven to work on this store
  // for hyphenated tags (tag:color-sorting).
  tagTerm: function(tag) {
    return /\s/.test(tag) ? 'tag:"' + tag.replace(/"/g, '\\"') + '"' : 'tag:' + tag;
  },

  excludeTags: function() {
    var raw = window.oaLogoVersionsExcludeTags;
    if (!Array.isArray(raw)) raw = raw ? String(raw).split(',') : [];
    // oaComingSoonHideTag is only defined when the setting is non-blank, and its products
    // are served with the size picker and add-to-cart suppressed — nothing that can be
    // offered as an alternative.
    if (typeof window.oaComingSoonHideTag === 'string' && window.oaComingSoonHideTag) {
      raw = raw.concat(window.oaComingSoonHideTag.split(','));
    }
    return raw.map(function(t) { return String(t).trim(); }).filter(Boolean);
  },

  // Applied to results as well as to the query: the handle fallback and the per-colour
  // fetch are exact lookups, so no query term covers them.
  isExcluded: function(tags) {
    var excluded = this.excludeTags();
    if (!excluded.length || !Array.isArray(tags)) return false;
    for (var i = 0; i < excluded.length; i++) {
      if (this.hasTag(tags, excluded[i])) return true;
    }
    return false;
  },

  // handle -> { group, role, siblings }, every version pointing at the same group, built
  // once from OA_LOGO_VERSION_GROUPS. Cached on the object; null until first use.
  _versionIndex: null,

  versionIndex: function() {
    // Guarded on the key count, not on truthiness: memoising an empty {} would let a call
    // landing before oa-logo-versions-map.js executes disable the map for the whole page.
    if (this._versionIndex && Object.keys(this._versionIndex).length) return this._versionIndex;
    var self = this;
    var index = {};
    var groups = window.OA_LOGO_VERSION_GROUPS;

    if (Array.isArray(groups)) {
      groups.forEach(function(group) {
        if (!group || typeof group !== 'object') return;
        var handles = {};
        var order = [];
        self.ROLES.forEach(function(role) {
          var handle = String(group[role] || '').trim().toLowerCase();
          // A blank key, or the same handle under two roles, is a generator bug and not a
          // version. Dropping it keeps the other groups working rather than taking the
          // feature down with it.
          if (!handle || order.indexOf(handle) !== -1) return;
          handles[role] = handle;
          order.push(handle);
        });
        // One version is not a choice: nothing to switch to, so nothing to render.
        if (order.length < 2) return;

        self.ROLES.forEach(function(role) {
          var handle = handles[role];
          if (!handle || index[handle]) return;
          index[handle] = {
            group: handles,
            role: role,
            siblings: order.filter(function(other) { return other !== handle; })
          };
        });
      });
    }

    this._versionIndex = index;
    return index;
  },

  // The generated grouping for a handle, or null when it is not listed.
  mappedGroup: function(handle) {
    if (!handle) return null;
    return this.versionIndex()[String(handle).trim().toLowerCase()] || null;
  },

  /**
   * Which branding version a product is, most reliable source first.
   *
   * The map only lists a colour that exists in two or more versions, so a colour that is
   * white-logo only is absent from it and still has to be placed — otherwise it is treated
   * as version-neutral and shows up in every rail. The handle is what answers there: it is
   * the join key the map itself is generated from, and it carries the marker even where the
   * title does not. Live example: the grey-logo and new-branding products are both titled
   * "EnhanceLift™ … Bralette | Black", with only the white one saying "with White Logo".
   *
   * @returns {string|null} 'white', 'grey', 'updated', or null when nothing says.
   */
  roleOf: function(handle, title) {
    var mapped = this.mappedGroup(handle);
    if (mapped) return mapped.role;
    return this.handleRole(handle) || this.parseTitle(title).marker;
  },

  /**
   * The role a handle states on its own, or null. Padded both ends so a phrase is matched as
   * whole hyphen-delimited words: '-white-logo-' hits "…-white-logo-6-black" but not
   * "…-bralette-white", where white is the colour.
   */
  handleRole: function(handle) {
    var padded = '-' + String(handle || '').trim().toLowerCase() + '-';
    for (var i = 0; i < this.MARKERS.length; i++) {
      if (padded.indexOf('-' + this.MARKERS[i].phrase.split(' ').join('-') + '-') !== -1) {
        return this.MARKERS[i].role;
      }
    }
    return null;
  },

  // The candidate handles for a sibling: the map first, then the "-2" suffix convention.
  siblingHandles: function(handle) {
    var mapped = this.mappedGroup(handle);
    if (mapped) return mapped.siblings.slice();
    if (/-2$/.test(handle)) return [handle.replace(/-2$/, '')];
    return [handle + '-2'];
  },

  /**
   * Which branding version the current product is, for the two paths the map does not cover:
   * the title search and the "-2" handle convention. A stated marker wins over createdAt,
   * which only infers and would get the pair backwards if a colour were added to the old
   * branding after the new branding shipped.
   *
   * The handle is asked before the title: titles are being edited to drop the logo suffix,
   * so a title marker is present for one era of the catalogue only, while the handle keeps
   * it. Where neither states anything, createdAt can still only answer 'updated' or 'grey' —
   * 'grey' being "the pre-rebrand one", which it is for all but four families, and all four
   * of those are in the map and do not come through here.
   * @private
   */
  assignRoles: function(current, alternative) {
    var currentMarker = this.handleRole(current.handle) || this.parseTitle(current.title).marker;
    var altMarker = this.handleRole(alternative.handle) || this.parseTitle(alternative.title).marker;

    if (currentMarker && !altMarker) {
      return currentMarker;
    }
    if (altMarker && !currentMarker) {
      return altMarker === 'updated' ? 'grey' : 'updated';
    }
    if (currentMarker && altMarker && currentMarker !== altMarker) {
      return currentMarker;
    }

    var currentAt = Date.parse(current.createdAt);
    var altAt = Date.parse(alternative.createdAt);
    // Without a usable date on both and no marker there is no way to say which logo is
    // which, and labelling them the wrong way round is worse than not offering the switch.
    if (isNaN(currentAt) || isNaN(altAt) || currentAt === altAt) return null;
    return currentAt < altAt ? 'grey' : 'updated';
  },

  /**
   * The original path: search by title, then pull details for the two that survive.
   * @private
   */
  resolveFromSearch: function(handle, searchQuery, mine, tag) {
    var self = this;
    return OAStorefrontApi.fetch(this.searchQuery, { searchQuery: searchQuery })
      .then(function(data) {
        var nodes = data && data.products && data.products.nodes ? data.products.nodes : [];
        var ids = self.pickCandidateIds(nodes, handle, mine, tag);
        if (!ids) return self.byHandleFallback(handle, tag);

        return OAStorefrontApi.fetch(self.detailsQuery(), { ids: [ids.currentId, ids.alternativeId] })
          .then(function(details) {
            var list = details && details.nodes ? details.nodes : [];
            var pair = self.buildVersions(list, handle, tag, false, mine);
            if (pair && pair.alternative) pair.colours = ids.colours || [];
            return pair;
          })
          .then(function(pair) {
            return pair || self.byHandleFallback(handle, tag);
          });
      });
  },

  /**
   * The mapped path: one exact handle lookup per version, and the title search alongside
   * purely for the colour family. Run in parallel — the colour list is a nicety and must not
   * delay or be able to fail the pairing.
   *
   * The colour search is skipped entirely for a three-version group. `colours` has two
   * consumers, both in the variants that show a single alternative and both of which stand
   * down when there are two, so there is no version whose colour family it would be.
   * @private
   */
  resolveFromMap: function(handle, mapped, searchQuery) {
    var self = this;
    var tag = window.oaLogoVersionsTag;
    var wantsColours = mapped.siblings.length === 1;

    var versionsFetch = OAStorefrontApi
      .fetch(this.buildHandleQuery([handle].concat(mapped.siblings)), {})
      .then(function(byHandle) {
        if (!byHandle) return null;
        var list = Object.keys(byHandle).map(function(k) { return byHandle[k]; });
        // acceptAnySibling and skipTagCheck: we asked for these by name, and the map is the
        // assertion that they belong together.
        return self.buildVersions(list, handle, tag, true, null, true);
      })
      .catch(function() { return null; });

    var coloursFetch = wantsColours
      ? OAStorefrontApi.fetch(this.searchQuery, { searchQuery: searchQuery })
        .then(function(data) {
          return data && data.products && data.products.nodes ? data.products.nodes : [];
        })
        .catch(function() { return []; })
      : Promise.resolve([]);

    return Promise.all([versionsFetch, coloursFetch]).then(function(results) {
      var pair = results[0];
      if (!pair) {
        // A handle in the map that the catalogue does not have. Silent on the storefront,
        // but a reviewer running a preview should see why nothing rendered.
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[logo-versions] mapped group did not resolve:', handle, mapped.group);
        }
        return null;
      }
      if (pair.alternative) {
        pair.colours = self.siblingColours(results[1], handle, {
          handle: pair.alternative.handle,
          parsed: self.parseTitle(pair.alternative.title)
        });
      }
      return pair;
    });
  },

  /**
   * Resolve the branding versions of the product on screen — two of them, or three.
   *
   * @param {string} handle - the handle currently on the PDP
   * @param {string} title - its full title, colour half included
   * @returns {Promise<Object|null>} see viewFor for the shape. Resolves null when there is no
   *   sibling, and never rejects — a failed lookup means the test simply does not render.
   */
  findAlternative: function(handle, title) {
    var self = this;
    if (!handle || !title || !window.OAStorefrontApi) return Promise.resolve(null);

    var cached = this.cacheGet(handle);
    if (cached !== undefined) return Promise.resolve(cached);
    if (this.inflight[handle]) return this.inflight[handle];

    var tag = window.oaLogoVersionsTag;
    // The map is the pairing path for most of the catalogue and those products are not
    // tagged, so a missing logo_versions_tag must not stand the feature down for them — it
    // only ever gated the title-search path.
    var mapped = this.mappedGroup(handle);
    if (!tag && !mapped) return Promise.resolve(null);

    var mine = this.parseTitle(title);
    if (!mine.base) return Promise.resolve(null);

    // Search on the MARKER-STRIPPED base, so the query finds the sibling whichever side we
    // are standing on: from "… Tank Top New Branding" the prefix is still "… Tank Top",
    // which matches both. Searching the raw title would only ever find our own version.
    var prefix = mine.rawBase;
    this.MARKERS.forEach(function(entry) {
      var phrase = entry.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      prefix = prefix.replace(new RegExp(phrase, 'ig'), ' ');
    });
    prefix = prefix.replace(/\s+/g, ' ').trim().replace(/"/g, '\\"');
    if (!prefix) return Promise.resolve(null);

    // Title only, no tag term. multiple-logo-versions marks a product that HAS a
    // counterpart, so colours that exist in one branding version only are untagged — and
    // ANDing the tag here returned a colour list of exactly one. The tag is still required
    // to call two products a pair, in pickCandidateIds; it just no longer decides which
    // colours the alternative is offered in.
    var searchQuery = 'title:"' + prefix + '*"';
    this.excludeTags().forEach(function(tag) {
      searchQuery += ' AND NOT ' + self.tagTerm(tag);
    });

    // The map wins: the versions come from the handles alone, and the title search is
    // demoted to building the colour family, so a failed or empty one costs the colour
    // selector rather than the pairing.
    var request = (mapped
      ? this.resolveFromMap(handle, mapped, searchQuery)
      : this.resolveFromSearch(handle, searchQuery, mine, tag)
    ).then(function(result) {
      self.cacheSet(handle, result);
      // Cache each sibling's own view too: tabbing across the versions and back is one round
      // trip, not one per version. Built by viewFor rather than by swapping fields, because
      // with three versions there is no single "alternative" to swap, and `colours` belongs
      // to the version it was resolved for and not to the others.
      if (result) {
        result.versions.forEach(function(entry) {
          if (entry.product.handle === handle) return;
          if (self.cacheGet(entry.product.handle) !== undefined) return;
          self.cacheSet(entry.product.handle, self.viewFor(entry.product.handle, result.versions));
        });
      }
      return result;
    }).catch(function() {
      return null;
    });

    // Store a non-rejecting copy so a second caller waiting on this request cannot inherit
    // its failure; every path here already falls back to null.
    var tracked = request.catch(function() { return null; });
    this.inflight[handle] = tracked;
    tracked.then(function() {
      if (self.inflight[handle] === tracked) delete self.inflight[handle];
    });

    return request;
  },

  /**
   * Narrow the search hits to the two products worth fetching in full: ours, and the one
   * sibling that is the same garment in the same colour.
   * @private
   */
  pickCandidateIds: function(nodes, handle, mine, tag) {
    var self = this;
    var currentId = null;
    var alternativeId = null;
    var alternativeHandle = null;
    var alternativeParsed = null;
    var all = [];

    nodes.forEach(function(node) {
      if (!node || self.isExcluded(node.tags)) return;
      all.push(node);
      // Pairing still demands the tag on both sides — that is what the tag is for.
      if (!self.hasTag(node.tags, tag)) return;
      if (node.handle === handle) {
        currentId = node.id;
        return;
      }
      if (alternativeId) return;
      if (self.isPair(mine, self.parseTitle(node.title))) {
        alternativeId = node.id;
        alternativeHandle = node.handle;
        alternativeParsed = self.parseTitle(node.title);
      }
    });

    if (!currentId || !alternativeId) return null;
    return {
      currentId: currentId,
      alternativeId: alternativeId,
      // Every OTHER colour of the branding version we are pointing at. Ids and titles only:
      // the search is deliberately light, and fetching variants for a 20-colour family would
      // put the document over the complexity budget. Details are pulled per colour on demand.
      colours: self.siblingColours(all, handle, {
        handle: alternativeHandle,
        parsed: alternativeParsed
      })
    };
  },

  /**
   * Full record for one colour the search already identified, fetched on demand.
   *
   * The colour list from findAlternative carries ids and titles only. Pulling variants for
   * a 20-colour family up front would blow the complexity budget for a control most
   * shoppers never touch, so a colour is loaded when it is actually picked.
   *
   * @param {string} gid - product id from the `colours` list
   * @returns {Promise<Object|null>} transformed product, or null
   */
  fetchColour: function(gid) {
    var self = this;
    if (!gid || !window.OAStorefrontApi) return Promise.resolve(null);

    var key = 'colour:' + gid;
    var cached = this.cacheGet(key);
    if (cached !== undefined) return Promise.resolve(cached);
    if (this.inflight[key]) return this.inflight[key];

    var request = OAStorefrontApi.fetch(this.detailsQuery(), { ids: [gid] })
      .then(function(details) {
        var node = details && details.nodes ? details.nodes[0] : null;
        if (node && self.isExcluded(node.tags)) node = null;
        var product = self.transform(node);
        self.cacheSet(key, product);
        return product;
      })
      .catch(function() { return null; });

    var tracked = request.catch(function() { return null; });
    this.inflight[key] = tracked;
    tracked.then(function() {
      if (self.inflight[key] === tracked) delete self.inflight[key];
    });

    return request;
  },

  /**
   * The colour family of one branding version, from the light search hits.
   *
   * Where the map knows the version being collected for, it is authoritative: a node belongs
   * only if the map places it under the SAME role, and one the map does not know is left out
   * rather than guessed at. Add it to oa-logo-versions-map.js to have it offered. Falling
   * back to the title marker there is what used to put a white-logo colour into a grey-logo
   * rail, back when the two parsed as one role.
   *
   * Only the title says anything for an unmapped version — every colour of "… New Branding |
   * X" parses as 'updated', every colour of "… | X" as null — and where neither side carries
   * a marker (the "-2" naming) the handle suffix stands in for it.
   *
   * @param {Array} nodes - search hits
   * @param {string} currentHandle - the PDP's own handle, never its own sibling
   * @param {{handle: string, parsed: Object}} target - the version to collect colours of
   * @private
   */
  siblingColours: function(nodes, currentHandle, target) {
    var self = this;
    if (!target || !target.handle || !target.parsed) return [];
    var targetParsed = target.parsed;
    var targetSuffixed = /-2$/.test(target.handle);
    var targetMapped = this.mappedGroup(target.handle);
    var out = [];

    nodes.forEach(function(node) {
      if (node.handle === currentHandle) return;
      var parsed = self.parseTitle(node.title);
      if (parsed.base !== targetParsed.base) return;

      if (targetMapped) {
        var mapped = self.mappedGroup(node.handle);
        if (!mapped || mapped.role !== targetMapped.role) return;
      } else {
        if (parsed.marker !== targetParsed.marker) return;
        if (parsed.marker === null && /-2$/.test(node.handle) !== targetSuffixed) return;
      }

      if (!parsed.colour) return;
      out.push({
        id: node.id,
        handle: node.handle,
        title: node.title,
        colour: parsed.colour
      });
    });

    return out;
  },

  /**
   * Last resort for pairs named by the "-2" convention rather than paired by title.
   * @private
   */
  byHandleFallback: function(handle, tag) {
    var self = this;
    var candidates = this.siblingHandles(handle);
    return OAStorefrontApi.fetch(this.buildHandleQuery([handle].concat(candidates)), {})
      .then(function(byHandle) {
        if (!byHandle) return null;
        var list = Object.keys(byHandle).map(function(k) { return byHandle[k]; });
        // Titles are not re-checked here: we asked for these handles by name, so whatever
        // comes back that is not the current product IS the sibling. Re-testing the title
        // would fail for exactly the pairs this fallback exists to cover.
        return self.buildVersions(list, handle, tag, true, null);
      })
      .catch(function() { return null; });
  },

  /**
   * Turn a list of full product records into the resolved version set.
   * @private
   */
  buildVersions: function(nodes, handle, tag, acceptAnySibling, mine, skipTagCheck) {
    var self = this;
    var byHandle = {};
    var current = null;

    nodes.forEach(function(node) {
      // isExcluded stays, always: the map asserts these are the same garment, not that any
      // of them is sellable, so preview-launch and coming-soon are still filtered.
      if (!node || self.isExcluded(node.tags)) return;
      var product = self.transform(node);
      if (!product) return;
      // Listing a group IS the assertion that they are versions of each other, so the tag is
      // not demanded of them — the map exists for products that are not tagged.
      if (!skipTagCheck && !self.hasTag(product.tags, tag)) return;
      if (product.handle === handle) {
        current = product;
        return;
      }
      if (!acceptAnySibling && !(mine && self.isPair(mine, self.parseTitle(product.title)))) return;
      byHandle[product.handle] = product;
    });
    if (!current) return null;

    var mapped = this.mappedGroup(current.handle);
    var versions = [];

    if (mapped) {
      // The map states every role; assignRoles only infers one from a title marker or
      // created_at and cannot tell white from grey at all.
      this.ROLES.forEach(function(role) {
        var roleHandle = mapped.group[role];
        if (!roleHandle) return;
        var product = roleHandle === current.handle ? current : byHandle[roleHandle];
        if (product) versions.push({ role: role, product: product });
      });
    } else {
      var alternative = null;
      Object.keys(byHandle).forEach(function(key) {
        if (!alternative) alternative = byHandle[key];
      });
      if (!alternative) return null;
      var currentRole = this.assignRoles(current, alternative);
      if (!currentRole) return null;
      // Same order as assignRoles: what the sibling's handle states beats the complement,
      // which cannot tell a white logo from a grey one. It must still differ from the role
      // we just gave the current product — the "-2" convention pairs a handle with itself
      // plus a suffix, so both carry the same marker, and two equal roles would push one
      // entry and drop the selector entirely.
      var alternativeRole = this.handleRole(alternative.handle);
      if (!alternativeRole || alternativeRole === currentRole) {
        alternativeRole = currentRole === 'updated' ? 'grey' : 'updated';
      }
      this.ROLES.forEach(function(role) {
        if (role === currentRole) versions.push({ role: role, product: current });
        else if (role === alternativeRole) versions.push({ role: role, product: alternative });
      });
    }

    if (versions.length < 2) return null;
    return this.viewFor(current.handle, versions);
  },

  /**
   * The resolved set as seen from one handle in it. Extracted so the mirror cache entries in
   * findAlternative are built by the same code that builds the answer.
   *
   * @returns {Object|null} { current, versions (white -> grey -> updated, current included),
   *   currentRole, alternative, alternativeRole, colours }. `alternative` is null when there
   *   are three versions: naming one of the two would hide the other from the callers that
   *   read it, so it doubles as the "there is no single alternative" signal. `colours` is
   *   non-empty only when `alternative` is set.
   * @private
   */
  viewFor: function(handle, versions) {
    var current = null;
    var others = [];
    versions.forEach(function(entry) {
      if (entry.product.handle === handle) current = entry;
      else others.push(entry);
    });
    if (!current) return null;

    return {
      current: current.product,
      versions: versions,
      currentRole: current.role,
      alternative: others.length === 1 ? others[0].product : null,
      alternativeRole: others.length === 1 ? others[0].role : null,
      colours: []
    };
  }
};
